/* eslint-disable max-len */
import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import Loading from '../../components/common/Loading'
import { filter } from 'lodash'
import {
  createCustomAlert,
  delCustomAlert,
  fetchCompanyService,
  fetchCustomAlertList,
  fetchMailDelivery,
  fetchSlackChannelList,
  updateCustomAlert,
  updateCustomAlertStatus,
} from '../../../states/containers/setting/systemSlice'
import { fetchTeamList } from '../../../states/containers/team/listSlice'
import { useAlert } from 'react-alert'
import ConfirmationModal from '../../components/setting/customAlert/ConfirmationModal'
import CustomAlertModal from '../../components/setting/customAlert/CustomAlertModal'
import { CustomAlert, patchCustomAlertRequest, postCustomAlertRequest } from '../../../states/types/setting'
import Modal from '../../components/common/Modal'
import { useParams } from 'react-router-dom'
import CustomAlertDetail from '../../components/setting/customAlert/CustomAlertDetail'
import CreateCustomAlertWizardModal from '../../components/setting/customAlert/CreateCustomAlertWizardModal'
import GroupButton from '../../components/common/GroupButton'

type LangValue = 'ja' | 'en'

interface MiniMenuState {
  display: boolean,
  x: number,
  y: number,
  id: number | undefined,
  status: string,
}

interface CustomAlertModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  customAlertDetail: CustomAlert | undefined,
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const CustomAlert: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    commonPart,
    system,
    teamList,
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [customAlertModalState, setCustomAlertModalState] = useState<CustomAlertModalState>({
    display: false,
    type: "",
    id: undefined,
    customAlertDetail: undefined,
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [isDisplayManagerMail, setIsDisplayManagerMail] = useState<boolean>(false)
  const [isDisplayMemberMail, setIsDisplayMemberMail] = useState<boolean>(false)

  const [isOpenWizard, setIsOpenWizard] = useState<boolean>(false)

  // custom alert modal
  const openCustomAlertModal = (event?: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>) => {
    const type = event?.currentTarget.dataset.type as string || "create"
    const id = Number(event?.currentTarget.dataset.id)
    const customAlertDetail = filter(system.custom_alert_list, { id: id })[0]
    setCustomAlertModalState({
      ...customAlertModalState,
      display: true,
      type: type,
      id: id,
      customAlertDetail: customAlertDetail,
    })
  }

  const closeCustomAlertModal = () => {
    setCustomAlertModalState({
      ...customAlertModalState,
      display: false,
    })
  }

  const duplicateCustomAlert = (id: number) => {
    const customAlertDetail = filter(system.custom_alert_list, { id: id })[0]
    setCustomAlertModalState({
      ...customAlertModalState,
      display: true,
      type: "duplicate",
      id: id,
      customAlertDetail: customAlertDetail,
    })
  }

  const deleteCustomAlert = (id: number) => {
    dispatch(
      delCustomAlert(
        {
          id: id,
        },
        () => {
          dispatch(fetchCustomAlertList())
          alert.success("アラートを削除しました")
        },
      ),
    )
  }

  const changeAlertStatus = (id: number, status: string) => {
    dispatch(
      updateCustomAlertStatus(
        {
          id: id,
          status: status,
        },
        () => {
          dispatch(fetchCustomAlertList())
          if (status === "started") alert.success("アラートを開始しました")
          if (status === "stopped") alert.success("アラートを停止しました")
        },
      ),
    )
  }

  const openConfirmationModal = (type: string, id: number) => {
    const customAlert = filter(system.custom_alert_list, { id: id })[0]
    let callback = () => {
      // none
    }
    switch (type) {
      case "stop":
        callback = () => changeAlertStatus(id, "stopped")
        break
      case "delete":
        callback = () => deleteCustomAlert(id)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: customAlert.name,
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const addCustomAlert = (params: postCustomAlertRequest, callback?: VoidFunction) => {
    dispatch(
      createCustomAlert(
        params,
        () => {
          dispatch(fetchCustomAlertList())
          alert.success("アラートを作成しました")
          if (callback) callback()
        },
      ),
    )
  }

  const modCustomAlert = (params: patchCustomAlertRequest) => {
    dispatch(
      updateCustomAlert(
        params,
        () => {
          dispatch(fetchCustomAlertList())
          alert.success("アラートを更新しました")
        },
      ),
    )
  }

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchCustomAlertList())
    dispatch(fetchCompanyService())
    dispatch(fetchMailDelivery())
  }, [login.accessToken])

  useEffect(() => {
    dispatch(
      fetchTeamList(
        () => {
          // none
        },
      ),
    )
  }, [commonPart.company])

  useEffect(() => {
    if (!system.mail_delivery?.slack_integration) return
    dispatch(fetchSlackChannelList())
  }, [system.mail_delivery?.slack_integration])

  return (
    <>
      {
        commonPart.isLoading || teamList.isLoading ? (
          <Loading />
        ) : (
          <section className="setting_page">
            <Helmet>
              <title>Qasee - カスタムアラート</title>
            </Helmet>
            <section className="page_header">
              <h1>
                カスタムアラート
              </h1>
            </section>
            <section className="page_description">
              <p>
                カスタムアラートを設定すると、条件に満たしたメンバーがいる時、対象チームのマネージャー全員にアラートメールが送信されます。
              </p>
              <p>
                送信されるメールのサンプルはこちら
                <span
                  className="sample_mail_link"
                  role="none"
                  onClick={() => setIsDisplayManagerMail(true)}
                >
                  マネージャーに送信するアラートメール
                </span>
                <span
                  className="sample_mail_link"
                  role="none"
                  onClick={() => setIsDisplayMemberMail(true)}
                >
                  対象メンバーに送信するアラートメール
                </span>
              </p>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="custom_alert_button">
                  <GroupButton
                    list={[
                      {
                        icon: "plus",
                        text: "簡単アラート作成",
                        callback: () => setIsOpenWizard(true),
                      },
                      {
                        icon: "plus",
                        text: "手動でアラートを作成",
                        callback: () => openCustomAlertModal(),
                      },
                    ]}
                  />
                </div>
                <div
                  className="block-detail"
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  {
                    system.custom_alert_list?.length ? (
                      system.custom_alert_list.map((v) => (
                        <React.Fragment key={`custom_alert_detail_${v.id}`}>
                          <CustomAlertDetail
                            customAlert={v}
                            openCustomAlertModalCallback={(event) => openCustomAlertModal(event)}
                            duplicateCustomAlertCallback={(id) => duplicateCustomAlert(id)}
                            openConfirmationModalCallback={(type, id) => openConfirmationModal(type, id)}
                            changeAlertStatusCallback={(id, status) => changeAlertStatus(id, status)}
                          />
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            alignItems: "center",
                            gap: "24px",
                            fontSize: "30px",
                            color: "#CCCCCC",
                          }}
                        >
                          カスタムアラートはありません
                          <button type="button" className="button" onClick={openCustomAlertModal}>
                            アラートを作成
                          </button>
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
            </section>
            {/* 詳細モーダル */}
            <CustomAlertModal
              customAlertModalState={customAlertModalState}
              closeCallback={closeCustomAlertModal}
              createCallback={(params) => addCustomAlert(params)}
              updateCallback={(params) => modCustomAlert(params)}
            />
            {/* 確認モーダル */}
            <ConfirmationModal
              confirmationModalState={confirmationModalState}
              closeCallback={closeConfirmationModal}
            />
            {/* マネージャー向けメールサンプル */}
            <Modal
              show={isDisplayManagerMail}
              wrapperClass="mail_sample"
              closeCallback={() => setIsDisplayManagerMail(false)}
              closeable
            >
              <>
                <img src={`/static/images/custom_alert/manager_${language}.png`} alt="sample" />
              </>
            </Modal>
            {/* メンバー向けメールサンプル */}
            <Modal
              show={isDisplayMemberMail}
              wrapperClass="mail_sample"
              closeCallback={() => setIsDisplayMemberMail(false)}
              closeable
            >
              <>
                <img src={`/static/images/custom_alert/member_${language}.png`} alt="sample" />
              </>
            </Modal>
            <CreateCustomAlertWizardModal
              closeCallback={() => setIsOpenWizard(false)}
              addCallback={(params, callback) => addCustomAlert(params, callback)}
              isDisplay={isOpenWizard}
            />
          </section>
        )
      }
    </>
  )
}

export default CustomAlert
